import type { AdminV2EntitlementRecord } from "@/types/adminV2";

export const AI_INSIGHTS_ENTITLEMENT_KEY = "ai_insights";

export type AiInsightsResolvedFrom = "organisation" | "global" | "default";

export interface AiInsightsEntitlementState {
  organisationEntitlement: AdminV2EntitlementRecord | null;
  globalEntitlement: AdminV2EntitlementRecord | null;
  resolvedFrom: AiInsightsResolvedFrom;
  effectiveAiInsightsEnabled: boolean;
}

/**
 * Resolve the effective AI Insights entitlement for an organisation.
 *
 * Precedence is explicit org override -> global default -> disabled-by-default.
 * The disabled fallback is intentional so missing entitlement rows never grant access implicitly.
 */
export const resolveAiInsightsEntitlementState = (args: {
  entitlements: AdminV2EntitlementRecord[] | undefined;
  scopedOrganisationId: string;
  entitlementKey: string;
}): AiInsightsEntitlementState => {
  if (!args.entitlements || args.scopedOrganisationId.length === 0) {
    return {
      organisationEntitlement: null,
      globalEntitlement: null,
      resolvedFrom: "default",
      effectiveAiInsightsEnabled: false,
    };
  }

  const organisationEntitlement =
    args.entitlements.find(
      (entitlement) =>
        entitlement.entitlementKey === args.entitlementKey &&
        entitlement.organisationId === args.scopedOrganisationId,
    ) ?? null;

  const globalEntitlement =
    args.entitlements.find(
      (entitlement) =>
        entitlement.entitlementKey === args.entitlementKey &&
        entitlement.organisationId === null,
    ) ?? null;

  const resolvedFrom: AiInsightsResolvedFrom = organisationEntitlement
    ? "organisation"
    : globalEntitlement
      ? "global"
      : "default";
  const effectiveEntitlement = organisationEntitlement ?? globalEntitlement;

  return {
    organisationEntitlement,
    globalEntitlement,
    resolvedFrom,
    effectiveAiInsightsEnabled: effectiveEntitlement?.isEnabled === true,
  };
};

export const formatAiInsightsResolvedFromLabel = (
  resolvedFrom: AiInsightsResolvedFrom,
): string =>
  resolvedFrom === "organisation"
    ? "Org override"
    : resolvedFrom === "global"
      ? "Global"
      : "Default";

export const buildAiInsightsToggleSuccessMessage = (
  nextIsEnabled: boolean,
): string =>
  nextIsEnabled
    ? "AI insights enabled for the organisation."
    : "AI insights disabled for the organisation.";
